import {
  getOrbDefenseCell,
  ORB_DEFENSE_GRID_HEIGHT,
  ORB_DEFENSE_GRID_WIDTH,
  type OrbDefenseGridPoint,
} from "./orbDefenseGrid";

export type OrbDefenseTurretUpgrade = {
  cost: number;
  definitionIndex: number;
  rank: number;
  label: string;
  range: number;
  damage: number;
  cooldown: number;
  speed: number;
};

export type OrbDefenseTurretFamily =
  | "fast-blast"
  | "chain-shot"
  | "big-shot"
  | "ice-shot"
  | "beam-blast"
  | "electroshock"
  | "barrier";

export type OrbDefensePowerKind = "damage" | "area" | "ice" | "none";

export type OrbDefenseNorbKind =
  | "normo"
  | "swarmo"
  | "zoomo"
  | "tougho"
  | "flybo"
  | "bombo"
  | "chompo"
  | "irono"
  | "sneako"
  | "splitto"
  | "awesomeo";

export type OrbDefenseNorbDefinition = {
  kind: OrbDefenseNorbKind;
  label: string;
  energyReward: number;
  scoreReward: number;
  healthMultiplier: number;
  speed: number;
  swarmSize: number;
};

export type OrbDefenseTurretDefinition = {
  id: string;
  label: string;
  name: string;
  levelLabel: string;
  rank: number;
  family: OrbDefenseTurretFamily;
  description: string;
  power: string;
  powerKind: OrbDefensePowerKind;
  rangeLabel: string;
  sizeLabel: string;
  costLabel: string;
  totalCost: number;
  efficiency: string;
  purchaseRequirement: string;
  member: boolean;
  color: number;
  fillStyle: string;
  w: number;
  h: number;
  cost: number;
  speed: number;
  range: number;
  damage: number;
  cooldown: number;
  upgrades: OrbDefenseTurretUpgrade[];
};

export type OrbDefenseWaveConfig = {
  level: number;
  kind: OrbDefenseNorbKind;
  label: string;
  interval: number;
  amount: number;
  speed: number;
  health: number;
  energyReward: number;
  scoreReward: number;
  bossPrize: number;
  boss: boolean;
};

export type OrbDefenseHudTurret = {
  index: number;
  label: string;
  name: string;
  levelLabel: string;
  rank: number;
  family: OrbDefenseTurretFamily;
  description: string;
  power: string;
  rangeLabel: string;
  sizeLabel: string;
  costLabel: string;
  efficiency: string;
  purchaseRequirement: string;
  member: boolean;
  color: number;
  cost: number;
  range: number;
  damage: number;
  cooldown: number;
  affordable: boolean;
  selected: boolean;
};

export type OrbDefenseHudState = {
  status: "splash" | "building" | "playing" | "won" | "lost";
  wave: number;
  processedWave: number;
  energy: number;
  score: number;
  scoreBonus: number;
  timeRemaining: number;
  orbsAlive: number;
  totalOrbs: number;
  enemies: number;
  spawners: number;
  turretsPlaced: number;
  selectedTurretIndex: number;
  selectedTurretLabel: string;
  selectedTurretCost: number;
  message: string;
  turretOptions: OrbDefenseHudTurret[];
};

export type OrbDefenseProjection = "reset" | "top" | "bottom" | "front" | "back" | "left" | "right";

export type OrbDefenseControlDetail =
  | { action: "select-turret"; index: number }
  | { action: "advance-wave" }
  | { action: "camera-projection"; projection: OrbDefenseProjection };

export const ORB_DEFENSE_STATE_EVENT = "miles-systems:orb-defense-state";
export const ORB_DEFENSE_CONTROL_EVENT = "miles-systems:orb-defense-control";
export const ORB_DEFENSE_FRAME_RATE = 35;
export const ORB_DEFENSE_WAVE_SECONDS = 30;
export const ORB_DEFENSE_INITIAL_SCORE_BONUS = 3000;
export const ORB_DEFENSE_STARTING_ENERGY = 150;
export const ORB_DEFENSE_TIME_BONUS_POINTS_PER_SECOND = 50;
export const ORB_DEFENSE_SCORE_BONUS_ENERGY_DIVISOR = 20;
export const ORB_DEFENSE_PASSIVE_ENERGY_SECONDS = 20;
export const ORB_DEFENSE_PASSIVE_ENERGY_PER_ORB = 25;

const TURRET_LEVEL_LABELS = ["I", "II", "III", "IV", "V"] as const;

const TURRET_FAMILY_COLORS: Record<OrbDefenseTurretFamily, { color: number; fillStyle: string }> = {
  "fast-blast": { color: 0x2f62ff, fillStyle: "rgb(47,98,255)" },
  "chain-shot": { color: 0xaa4cff, fillStyle: "rgb(170,76,255)" },
  "big-shot": { color: 0x5bd83b, fillStyle: "rgb(91,216,59)" },
  "ice-shot": { color: 0x18d8ff, fillStyle: "rgb(24,216,255)" },
  "beam-blast": { color: 0xff7b22, fillStyle: "rgb(255,123,34)" },
  electroshock: { color: 0xffce42, fillStyle: "rgb(255,206,66)" },
  barrier: { color: 0x4da3ff, fillStyle: "rgb(77,163,255)" },
};

export const ORB_DEFENSE_NORBS: Record<OrbDefenseNorbKind, OrbDefenseNorbDefinition> = {
  normo: {
    kind: "normo",
    label: "Normo",
    energyReward: 2,
    scoreReward: 20,
    healthMultiplier: 1,
    speed: 0.045,
    swarmSize: 1,
  },
  swarmo: {
    kind: "swarmo",
    label: "Swarmo",
    energyReward: 1,
    scoreReward: 10,
    healthMultiplier: 0.52,
    speed: 0.058,
    swarmSize: 2,
  },
  zoomo: {
    kind: "zoomo",
    label: "Zoomo",
    energyReward: 3,
    scoreReward: 30,
    healthMultiplier: 0.88,
    speed: 0.092,
    swarmSize: 1,
  },
  tougho: {
    kind: "tougho",
    label: "Tougho",
    energyReward: 3,
    scoreReward: 30,
    healthMultiplier: 1.85,
    speed: 0.035,
    swarmSize: 1,
  },
  flybo: {
    kind: "flybo",
    label: "Flybo",
    energyReward: 4,
    scoreReward: 40,
    healthMultiplier: 0.9,
    speed: 0.072,
    swarmSize: 1,
  },
  bombo: {
    kind: "bombo",
    label: "Bombo",
    energyReward: 3,
    scoreReward: 30,
    healthMultiplier: 1.28,
    speed: 0.042,
    swarmSize: 1,
  },
  chompo: {
    kind: "chompo",
    label: "Chompo",
    energyReward: 3,
    scoreReward: 30,
    healthMultiplier: 1.34,
    speed: 0.046,
    swarmSize: 1,
  },
  irono: {
    kind: "irono",
    label: "Irono",
    energyReward: 4,
    scoreReward: 40,
    healthMultiplier: 2.2,
    speed: 0.033,
    swarmSize: 1,
  },
  sneako: {
    kind: "sneako",
    label: "Sneako",
    energyReward: 2,
    scoreReward: 20,
    healthMultiplier: 0.78,
    speed: 0.064,
    swarmSize: 1,
  },
  splitto: {
    kind: "splitto",
    label: "Splitto",
    energyReward: 26,
    scoreReward: 260,
    healthMultiplier: 2.6,
    speed: 0.036,
    swarmSize: 1,
  },
  awesomeo: {
    kind: "awesomeo",
    label: "Awesomeo",
    energyReward: 500,
    scoreReward: 5000,
    healthMultiplier: 8.8,
    speed: 0.022,
    swarmSize: 1,
  },
};

type TurretRowInput = {
  family: OrbDefenseTurretFamily;
  name: string;
  rank: number;
  description: string;
  power: string;
  powerKind: OrbDefensePowerKind;
  range: number;
  rangeLabel?: string;
  size: `${number}x${number}`;
  cost: number;
  totalCost: number;
  efficiency: string;
  purchaseRequirement: string;
  member?: boolean;
  cooldown?: number;
  speed?: number;
};

type OrbDefenseTurretDefinitionInput = Omit<OrbDefenseTurretDefinition, "upgrades">;

function powerValue(power: string) {
  const match = power.match(/\d+/);
  return match ? Number(match[0]) : 0;
}

function makeTurret(input: TurretRowInput): OrbDefenseTurretDefinitionInput {
  const [w, h] = input.size.split("x").map(Number);
  const color = TURRET_FAMILY_COLORS[input.family];
  const levelLabel = TURRET_LEVEL_LABELS[input.rank - 1] ?? String(input.rank);
  const damage = input.powerKind === "none" ? 0 : powerValue(input.power);
  return {
    id: `${input.family}-${input.rank}`,
    label: `${input.name} ${levelLabel}`,
    name: input.name,
    levelLabel,
    rank: input.rank,
    family: input.family,
    description: input.description,
    power: input.power,
    powerKind: input.powerKind,
    range: input.range,
    rangeLabel: input.rangeLabel ?? String(input.range),
    sizeLabel: input.size,
    cost: input.cost,
    totalCost: input.totalCost,
    costLabel: `${input.cost}/${input.totalCost}`,
    efficiency: input.efficiency,
    purchaseRequirement: input.purchaseRequirement,
    member: Boolean(input.member),
    color: color.color,
    fillStyle: color.fillStyle,
    w,
    h,
    damage,
    cooldown: input.cooldown ?? Math.max(5, 18 - input.rank * 2),
    speed: input.speed ?? 1,
  };
}

const TURRET_ROWS: OrbDefenseTurretDefinitionInput[] = [
  makeTurret({
    family: "fast-blast",
    name: "Fast Blast",
    rank: 1,
    description: "Cheap, but weak.",
    power: "5 Damage",
    powerKind: "damage",
    range: 30,
    size: "2x2",
    cost: 10,
    totalCost: 10,
    efficiency: "0.5",
    purchaseRequirement: "None",
    speed: 0.5,
  }),
  makeTurret({
    family: "fast-blast",
    name: "Fast Blast",
    rank: 2,
    description: "Cheap, but weak.",
    power: "10 Damage",
    powerKind: "damage",
    range: 30,
    size: "2x2",
    cost: 10,
    totalCost: 20,
    efficiency: "0.5",
    purchaseRequirement: "None",
    speed: 0.5,
  }),
  makeTurret({
    family: "fast-blast",
    name: "Fast Blast",
    rank: 3,
    description: "Cheap, but weak.",
    power: "20 Damage",
    powerKind: "damage",
    range: 30,
    size: "2x2",
    cost: 15,
    totalCost: 35,
    efficiency: "0.57",
    purchaseRequirement: "None",
    speed: 0.5,
  }),
  makeTurret({
    family: "fast-blast",
    name: "Turbo Blast",
    rank: 4,
    description: "Cheap, but weak.",
    power: "25 Damage",
    powerKind: "damage",
    range: 30,
    size: "2x2",
    cost: 15,
    totalCost: 50,
    efficiency: "0.5",
    purchaseRequirement: "Beat Wave 44 Boss",
    speed: 0.5,
  }),
  makeTurret({
    family: "fast-blast",
    name: "Nitro Blast",
    rank: 5,
    description: "Cheap, but weak.",
    power: "40 Damage",
    powerKind: "damage",
    range: 30,
    size: "2x2",
    cost: 30,
    totalCost: 80,
    efficiency: "0.5",
    purchaseRequirement: "Beat Wave 44 Boss",
    speed: 0.5,
  }),
  makeTurret({
    family: "chain-shot",
    name: "Chain Shot",
    rank: 1,
    description: "Short-ranged, yet bounces.",
    power: "12 Damage",
    powerKind: "damage",
    range: 8,
    size: "3x3",
    cost: 15,
    totalCost: 15,
    efficiency: "0.8",
    purchaseRequirement: "None",
    speed: 1.2,
  }),
  makeTurret({
    family: "chain-shot",
    name: "Chain Shot",
    rank: 2,
    description: "Short-ranged, yet bounces.",
    power: "16 Damage",
    powerKind: "damage",
    range: 10,
    size: "3x3",
    cost: 20,
    totalCost: 35,
    efficiency: "0.46",
    purchaseRequirement: "None",
    speed: 1.2,
  }),
  makeTurret({
    family: "chain-shot",
    name: "Chain Shot",
    rank: 3,
    description: "Short-ranged, yet bounces.",
    power: "22 Damage",
    powerKind: "damage",
    range: 12,
    size: "3x3",
    cost: 25,
    totalCost: 60,
    efficiency: "0.37",
    purchaseRequirement: "None",
    speed: 1.2,
  }),
  makeTurret({
    family: "chain-shot",
    name: "Thunder Shot",
    rank: 4,
    description: "Short-ranged, yet bounces.",
    power: "32 Damage",
    powerKind: "damage",
    range: 13,
    size: "3x3",
    cost: 30,
    totalCost: 90,
    efficiency: "0.36",
    purchaseRequirement: "Beat Wave 55 Boss",
    speed: 1.2,
  }),
  makeTurret({
    family: "chain-shot",
    name: "Storm Scraper",
    rank: 5,
    description: "Short-ranged, yet bounces.",
    power: "64 Damage",
    powerKind: "damage",
    range: 16,
    size: "3x3",
    cost: 40,
    totalCost: 130,
    efficiency: "0.49",
    purchaseRequirement: "Beat Wave 55 Boss",
    speed: 1.2,
  }),
  makeTurret({
    family: "big-shot",
    name: "Big Shot",
    rank: 1,
    description: "Powerful, with a large area of effect.",
    power: "40 Area",
    powerKind: "area",
    range: 20,
    size: "3x3",
    cost: 30,
    totalCost: 30,
    efficiency: "N/A",
    purchaseRequirement: "Beat Wave 11 Boss",
    cooldown: 26,
    speed: 1.4,
  }),
  makeTurret({
    family: "big-shot",
    name: "Big Shot",
    rank: 2,
    description: "Powerful, with a large area of effect.",
    power: "50 Area",
    powerKind: "area",
    range: 20,
    size: "3x3",
    cost: 45,
    totalCost: 75,
    efficiency: "N/A",
    purchaseRequirement: "Beat Wave 11 Boss",
    cooldown: 24,
    speed: 1.4,
  }),
  makeTurret({
    family: "big-shot",
    name: "Big Shot",
    rank: 3,
    description: "Powerful, with a large area of effect.",
    power: "70 Area",
    powerKind: "area",
    range: 20,
    size: "3x3",
    cost: 60,
    totalCost: 135,
    efficiency: "N/A",
    purchaseRequirement: "Beat Wave 11 Boss",
    cooldown: 22,
    speed: 1.4,
  }),
  makeTurret({
    family: "big-shot",
    name: "Mega Shot",
    rank: 4,
    description: "Powerful, with a large area of effect.",
    power: "80 Area",
    powerKind: "area",
    range: 25,
    size: "3x3",
    cost: 90,
    totalCost: 225,
    efficiency: "N/A",
    purchaseRequirement: "Beat Wave 66 Boss",
    cooldown: 20,
    speed: 1.4,
  }),
  makeTurret({
    family: "big-shot",
    name: "Atomic Shot",
    rank: 5,
    description: "Powerful, with a large area of effect.",
    power: "100 Area",
    powerKind: "area",
    range: 30,
    size: "3x3",
    cost: 120,
    totalCost: 345,
    efficiency: "N/A",
    purchaseRequirement: "Beat Wave 66 Boss",
    cooldown: 18,
    speed: 1.4,
  }),
  makeTurret({
    family: "ice-shot",
    name: "Ice Shot",
    rank: 1,
    description: "Slows down Norbs.",
    power: "1 Ice",
    powerKind: "ice",
    range: 6,
    size: "2x2",
    cost: 40,
    totalCost: 40,
    efficiency: "N/A",
    purchaseRequirement: "Beat Wave 22 Boss",
    cooldown: 22,
  }),
  makeTurret({
    family: "ice-shot",
    name: "Ice Shot",
    rank: 2,
    description: "Slows down Norbs.",
    power: "2 Ice",
    powerKind: "ice",
    range: 6,
    size: "2x2",
    cost: 75,
    totalCost: 115,
    efficiency: "N/A",
    purchaseRequirement: "Beat Wave 22 Boss",
    cooldown: 20,
  }),
  makeTurret({
    family: "ice-shot",
    name: "Ice Shot",
    rank: 3,
    description: "Slows down Norbs.",
    power: "3 Ice",
    powerKind: "ice",
    range: 6,
    size: "2x2",
    cost: 100,
    totalCost: 215,
    efficiency: "N/A",
    purchaseRequirement: "Beat Wave 22 Boss",
    cooldown: 18,
  }),
  makeTurret({
    family: "ice-shot",
    name: "Ice Beam",
    rank: 4,
    description: "Slows down Norbs.",
    power: "4 Ice",
    powerKind: "ice",
    range: 6,
    size: "2x2",
    cost: 150,
    totalCost: 365,
    efficiency: "N/A",
    purchaseRequirement: "Beat Wave 77 Boss",
    cooldown: 16,
  }),
  makeTurret({
    family: "ice-shot",
    name: "Blizzard Wave",
    rank: 5,
    description: "Slows down Norbs.",
    power: "5 Ice",
    powerKind: "ice",
    range: 6,
    size: "2x2",
    cost: 200,
    totalCost: 565,
    efficiency: "N/A",
    purchaseRequirement: "Beat Wave 77 Boss",
    cooldown: 14,
  }),
  makeTurret({
    family: "beam-blast",
    name: "Beam Blast",
    rank: 1,
    description: "The pinnacle of anti-Norb defences!",
    power: "200 Damage",
    powerKind: "damage",
    range: 30,
    size: "4x4",
    cost: 120,
    totalCost: 120,
    efficiency: "1.67",
    purchaseRequirement: "Beat Wave 33 Boss",
    cooldown: 18,
    speed: 2,
  }),
  makeTurret({
    family: "beam-blast",
    name: "Beam Blast",
    rank: 2,
    description: "The pinnacle of anti-Norb defences!",
    power: "250 Damage",
    powerKind: "damage",
    range: 30,
    size: "4x4",
    cost: 120,
    totalCost: 240,
    efficiency: "1.04",
    purchaseRequirement: "Beat Wave 33 Boss",
    cooldown: 16,
    speed: 2,
  }),
  makeTurret({
    family: "beam-blast",
    name: "Beam Blast",
    rank: 3,
    description: "The pinnacle of anti-Norb defences!",
    power: "333 Damage",
    powerKind: "damage",
    range: 30,
    size: "4x4",
    cost: 120,
    totalCost: 360,
    efficiency: "0.92",
    purchaseRequirement: "Beat Wave 33 Boss",
    cooldown: 14,
    speed: 2,
  }),
  makeTurret({
    family: "beam-blast",
    name: "Solar Blast",
    rank: 4,
    description: "The pinnacle of anti-Norb defences!",
    power: "500 Damage",
    powerKind: "damage",
    range: 30,
    size: "4x4",
    cost: 120,
    totalCost: 480,
    efficiency: "1.04",
    purchaseRequirement: "Beat Wave 88 Boss",
    cooldown: 12,
    speed: 2,
  }),
  makeTurret({
    family: "beam-blast",
    name: "Nova Blast",
    rank: 5,
    description: "The pinnacle of anti-Norb defences!",
    power: "1000 Damage",
    powerKind: "damage",
    range: 30,
    size: "4x4",
    cost: 120,
    totalCost: 600,
    efficiency: "1.67",
    purchaseRequirement: "Beat Wave 88 Boss",
    cooldown: 10,
    speed: 2,
  }),
  makeTurret({
    family: "electroshock",
    name: "Electroshock",
    rank: 1,
    description: "Stuns groups of Norbs (and reveals Sneakos!)",
    power: "5 Damage",
    powerKind: "damage",
    range: 8,
    rangeLabel: "N/A",
    size: "3x3",
    cost: 40,
    totalCost: 40,
    efficiency: "N/A",
    purchaseRequirement: "Beat Wave 22 Boss",
    member: true,
    cooldown: 22,
  }),
  makeTurret({
    family: "electroshock",
    name: "Electroshock",
    rank: 2,
    description: "Stuns groups of Norbs (and reveals Sneakos!)",
    power: "10 Damage",
    powerKind: "damage",
    range: 10,
    rangeLabel: "N/A",
    size: "3x3",
    cost: 75,
    totalCost: 115,
    efficiency: "N/A",
    purchaseRequirement: "Beat Wave 22 Boss",
    member: true,
    cooldown: 20,
  }),
  makeTurret({
    family: "electroshock",
    name: "Electroshock",
    rank: 3,
    description: "Stuns groups of Norbs (and reveals Sneakos!)",
    power: "20 Damage",
    powerKind: "damage",
    range: 12,
    rangeLabel: "N/A",
    size: "3x3",
    cost: 100,
    totalCost: 215,
    efficiency: "N/A",
    purchaseRequirement: "Beat Wave 22 Boss",
    member: true,
    cooldown: 18,
  }),
  makeTurret({
    family: "electroshock",
    name: "Lightning Bolt",
    rank: 4,
    description: "Stuns groups of Norbs (and reveals Sneakos!)",
    power: "40 Damage",
    powerKind: "damage",
    range: 13,
    rangeLabel: "N/A",
    size: "3x3",
    cost: 150,
    totalCost: 365,
    efficiency: "N/A",
    purchaseRequirement: "Beat Wave 77 Boss",
    member: true,
    cooldown: 16,
  }),
  makeTurret({
    family: "electroshock",
    name: "Storm Bolt",
    rank: 5,
    description: "Stuns groups of Norbs (and reveals Sneakos!)",
    power: "80 Damage",
    powerKind: "damage",
    range: 16,
    rangeLabel: "N/A",
    size: "3x3",
    cost: 200,
    totalCost: 565,
    efficiency: "N/A",
    purchaseRequirement: "Beat Wave 77 Boss",
    member: true,
    cooldown: 14,
  }),
  makeTurret({
    family: "barrier",
    name: "Barrier",
    rank: 1,
    description: "Deters Norbs, but takes damage if attacked!",
    power: "N/A",
    powerKind: "none",
    range: 0,
    rangeLabel: "N/A",
    size: "2x2",
    cost: 10,
    totalCost: 10,
    efficiency: "N/A",
    purchaseRequirement: "None",
    member: true,
    cooldown: 999,
  }),
];

export const ORB_DEFENSE_TURRETS: OrbDefenseTurretDefinition[] = TURRET_ROWS.map((turret, index) => {
  const upgradeIndex = TURRET_ROWS.findIndex((candidate) => candidate.family === turret.family && candidate.rank === turret.rank + 1);
  const upgradeTarget = upgradeIndex >= 0 ? TURRET_ROWS[upgradeIndex] : null;
  return {
    ...turret,
    upgrades: upgradeTarget
      ? [
          {
            definitionIndex: upgradeIndex,
            rank: upgradeTarget.rank,
            label: upgradeTarget.label,
            cost: upgradeTarget.cost,
            range: upgradeTarget.range,
            damage: upgradeTarget.damage,
            cooldown: upgradeTarget.cooldown,
            speed: upgradeTarget.speed,
          },
        ]
      : [],
  };
}) satisfies OrbDefenseTurretDefinition[];

export const ORB_DEFENSE_PLACEABLE_TURRET_INDEXES = ORB_DEFENSE_TURRETS.map((turret, index) =>
  turret.rank === 1 ? index : null,
).filter((index): index is number => index !== null);

export const ORB_DEFENSE_PLACEABLE_TURRETS = ORB_DEFENSE_PLACEABLE_TURRET_INDEXES.map((index) => ORB_DEFENSE_TURRETS[index]);

const FIRST_FIGHT_BOSS_WAVES: Partial<Record<number, OrbDefenseNorbKind>> = {
  11: "normo",
  22: "swarmo",
  33: "zoomo",
  44: "tougho",
  55: "flybo",
  66: "bombo",
  77: "chompo",
  88: "irono",
  99: "awesomeo",
};

function isFirstFightBossWave(level: number) {
  return Object.prototype.hasOwnProperty.call(FIRST_FIGHT_BOSS_WAVES, level);
}

function getRegularNorbKind(level: number): OrbDefenseNorbKind {
  if (level >= 89) {
    return level % 3 === 0 ? "sneako" : "irono";
  }
  if (level >= 78) {
    return "irono";
  }
  if (level >= 67) {
    return "chompo";
  }
  if (level >= 56) {
    return "bombo";
  }
  if (level >= 45) {
    return "flybo";
  }
  if (level >= 34) {
    return "tougho";
  }
  if (level >= 23) {
    return "zoomo";
  }
  if (level >= 12) {
    return "swarmo";
  }
  return "normo";
}

function getBossPrize(level: number) {
  if (!isFirstFightBossWave(level)) {
    return 0;
  }
  return 1000 + Math.floor(level / 11) * 1000;
}

function getRegularNorbHealth(level: number, definition: OrbDefenseNorbDefinition) {
  const baseHealth = 42 + level * 18 + Math.pow(level, 2) * 1.6;
  return Math.max(1, Math.round(baseHealth * definition.healthMultiplier));
}

function getRegularNorbAmount(level: number, definition: OrbDefenseNorbDefinition) {
  const baseAmount = Math.max(5, Math.round(level * 3.2));
  return Math.round(baseAmount * definition.swarmSize);
}

function getRegularSpawnInterval(level: number, definition: OrbDefenseNorbDefinition) {
  const baseInterval = Math.max(7, Math.round(35 - level * 0.44));
  return Math.max(5, Math.round(baseInterval / Math.max(1, definition.swarmSize * 0.82)));
}

export function createOrbDefenseWaveConfig(level: number): OrbDefenseWaveConfig {
  const normalizedLevel = Math.max(0, Math.round(level));
  if (normalizedLevel <= 0) {
    return {
      level: 0,
      kind: "normo",
      label: "Ready",
      interval: ORB_DEFENSE_WAVE_SECONDS * ORB_DEFENSE_FRAME_RATE,
      amount: 0,
      speed: 0,
      health: 0,
      energyReward: 0,
      scoreReward: 0,
      bossPrize: 0,
      boss: false,
    };
  }

  const bossKind = FIRST_FIGHT_BOSS_WAVES[normalizedLevel];
  if (bossKind) {
    const definition = ORB_DEFENSE_NORBS[bossKind];
    const bossScale = bossKind === "awesomeo" ? 1 : bossKind === "swarmo" ? 3.8 : 7.4;
    return {
      level: normalizedLevel,
      kind: definition.kind,
      label: `${definition.label} Boss`,
      interval: bossKind === "swarmo" ? 22 : 0,
      amount: bossKind === "swarmo" ? 4 : 1,
      speed: Math.max(0.018, definition.speed * 0.64),
      health: Math.round(getRegularNorbHealth(normalizedLevel, definition) * bossScale + normalizedLevel * 115),
      energyReward: bossKind === "awesomeo" ? definition.energyReward : definition.energyReward * 25,
      scoreReward: bossKind === "awesomeo" ? definition.scoreReward : definition.scoreReward * 25,
      bossPrize: getBossPrize(normalizedLevel),
      boss: true,
    };
  }

  const definition = ORB_DEFENSE_NORBS[getRegularNorbKind(normalizedLevel)];
  return {
    level: normalizedLevel,
    kind: definition.kind,
    label: definition.label,
    interval: getRegularSpawnInterval(normalizedLevel, definition),
    amount: getRegularNorbAmount(normalizedLevel, definition),
    speed: definition.speed,
    health: getRegularNorbHealth(normalizedLevel, definition),
    energyReward: definition.energyReward,
    scoreReward: definition.scoreReward,
    bossPrize: 0,
    boss: false,
  };
}

export function getOrbDefenseMonsterReward(config: Pick<OrbDefenseWaveConfig, "energyReward" | "scoreReward">) {
  return {
    energy: config.energyReward,
    score: config.scoreReward,
  };
}

export function getOrbDefenseScoreBonusEnergy(scoreBonus: number, orbsAlive: number) {
  return Math.floor(Math.max(0, scoreBonus) / ORB_DEFENSE_SCORE_BONUS_ENERGY_DIVISOR) * Math.max(0, orbsAlive);
}

export function getOrbDefenseTimeSkipBonus(timeRemaining: number) {
  return Math.max(0, Math.round(timeRemaining * ORB_DEFENSE_TIME_BONUS_POINTS_PER_SECOND));
}

export function sortOrbDefenseTargetsBySpawnDistance(
  targets: OrbDefenseGridPoint[],
  spawns: OrbDefenseGridPoint[],
) {
  return [...targets].sort((a, b) => averageDistance(a, spawns) - averageDistance(b, spawns));
}

function averageDistance(point: OrbDefenseGridPoint, spawns: OrbDefenseGridPoint[]) {
  return (
    spawns.reduce((sum, spawn) => sum + Math.hypot(spawn.x - point.x, spawn.y - point.y), 0) /
    Math.max(1, spawns.length)
  );
}

const pathCache = new Map<string, OrbDefenseGridPoint[]>();

function pathCacheKey(start: OrbDefenseGridPoint, end: OrbDefenseGridPoint) {
  return `${start.x},${start.y}->${end.x},${end.y}`;
}

export function findOrbDefensePath(start: OrbDefenseGridPoint, end: OrbDefenseGridPoint): OrbDefenseGridPoint[] {
  const normalizedStart = normalizeGridPoint(start);
  const normalizedEnd = normalizeGridPoint(end);
  const cacheKey = pathCacheKey(normalizedStart, normalizedEnd);
  const cached = pathCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  if (
    getOrbDefenseCell(normalizedStart.x, normalizedStart.y) !== 0 ||
    getOrbDefenseCell(normalizedEnd.x, normalizedEnd.y) !== 0
  ) {
    return [];
  }

  const queue: OrbDefenseGridPoint[] = [normalizedStart];
  const parents = new Map<string, string | null>([[pointKey(normalizedStart), null]]);

  for (let index = 0; index < queue.length; index += 1) {
    const current = queue[index];
    if (current.x === normalizedEnd.x && current.y === normalizedEnd.y) {
      const path = rebuildPath(parents, normalizedEnd);
      pathCache.set(cacheKey, path);
      return path;
    }

    for (const neighbor of getPathNeighbors(current)) {
      const key = pointKey(neighbor);
      if (parents.has(key)) {
        continue;
      }
      parents.set(key, pointKey(current));
      queue.push(neighbor);
    }
  }

  return [];
}

function normalizeGridPoint(point: OrbDefenseGridPoint): OrbDefenseGridPoint {
  return {
    x: Math.max(0, Math.min(ORB_DEFENSE_GRID_WIDTH - 1, Math.round(point.x))),
    y: Math.max(0, Math.min(ORB_DEFENSE_GRID_HEIGHT - 1, Math.round(point.y))),
  };
}

function pointKey(point: OrbDefenseGridPoint) {
  return `${point.x},${point.y}`;
}

function pointFromKey(key: string): OrbDefenseGridPoint {
  const [x, y] = key.split(",").map(Number);
  return { x, y };
}

function rebuildPath(parents: Map<string, string | null>, end: OrbDefenseGridPoint) {
  const path: OrbDefenseGridPoint[] = [];
  let current: string | null | undefined = pointKey(end);

  while (current) {
    path.unshift(pointFromKey(current));
    current = parents.get(current);
  }

  return path;
}

function getPathNeighbors(point: OrbDefenseGridPoint) {
  const candidates = [
    { x: point.x + 1, y: point.y },
    { x: point.x, y: point.y + 1 },
    { x: point.x - 1, y: point.y },
    { x: point.x, y: point.y - 1 },
  ];

  return candidates.filter(
    (candidate) =>
      candidate.x >= 0 &&
      candidate.x < ORB_DEFENSE_GRID_WIDTH &&
      candidate.y >= 0 &&
      candidate.y < ORB_DEFENSE_GRID_HEIGHT &&
      getOrbDefenseCell(candidate.x, candidate.y) === 0,
  );
}
